import { prefersReducedMotion } from "../lib/dom";

/**
 * Menu motion for the title screen. Panels move on smooth ease-out curves
 * (stepped timing is kept for in-world pixel props only, where it reads as
 * retro instead of as dropped frames). Everything animates transform /
 * opacity, plus a height tween on the menu window when its content changes.
 */

const EASE = "cubic-bezier(.2,.8,.2,1)";

/** How the window's content just changed; decides the direction it slides in from. */
export type SwapKind = "enter" | "tab" | "forward" | "back" | "fade";

const OFFSET: Record<SwapKind, (dir: number) => string> = {
  enter: () => "translateY(14px)",
  tab: (dir) => `translateX(${dir * 28}px)`,
  forward: () => "translateX(28px)",
  back: () => "translateX(-28px)",
  fade: () => "none",
};

/**
 * FLIP: `el` already sits at its new layout; animate it from where it was
 * (`first`) by inverting the difference with a transform.
 */
export function flipFrom(el: HTMLElement, first: DOMRect, duration = 440): void {
  if (prefersReducedMotion()) return;
  const last = el.getBoundingClientRect();
  if (!last.width || !first.width) return;
  const s = first.width / last.width;
  const dx = first.left - last.left;
  const dy = first.top - last.top;
  el.animate(
    [
      { transformOrigin: "top left", transform: `translate(${dx}px, ${dy}px) scale(${s})` },
      { transformOrigin: "top left", transform: "none" },
    ],
    { duration, easing: EASE },
  );
}

/** Window rises in when the menu first appears. */
export function windowIn(win: HTMLElement): void {
  if (prefersReducedMotion()) return;
  win.animate(
    [
      { opacity: 0, transform: "translateY(28px)" },
      { opacity: 1, transform: "none" },
    ],
    {
      duration: 380,
      delay: 90,
      easing: EASE,
      fill: "backwards",
    },
  );
}

/**
 * Keeps the menu window's height tween and content slide in sync with
 * re-renders (department switch, level intro, back).
 */
export class WindowMotion {
  /** Last height actually on screen (updated by a ResizeObserver, so mid-tween values count). */
  private shownHeight = 0;
  private heightAnim: Animation | null = null;

  constructor(private readonly win: HTMLElement) {
    new ResizeObserver(() => {
      this.shownHeight = win.getBoundingClientRect().height;
    }).observe(win);
  }

  /** Call right after the window's content was replaced. */
  swapped(kind: SwapKind, dir: number): void {
    if (prefersReducedMotion()) return;
    const win = this.win;
    const from = this.shownHeight;
    this.heightAnim?.cancel();
    const to = win.getBoundingClientRect().height;
    if (kind !== "enter" && from > 0 && Math.abs(to - from) > 2) {
      win.style.overflow = "hidden";
      this.heightAnim = win.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 260, easing: EASE });
      this.heightAnim.onfinish = this.heightAnim.oncancel = () => {
        win.style.overflow = "";
      };
    }
    const offset = OFFSET[kind](dir || 1);
    let delay = kind === "enter" ? 160 : 0;
    for (const el of win.children) {
      // The tab row stays put while switching departments; only the content below it moves.
      if (kind === "tab" && el.classList.contains("depts")) continue;
      const parts = el.classList.contains("levels") ? [...el.children] : [el];
      for (const p of parts) {
        p.animate(
          [
            { opacity: 0, transform: offset },
            { opacity: 1, transform: "none" },
          ],
          {
            duration: kind === "enter" ? 320 : 240,
            delay,
            easing: EASE,
            fill: "backwards",
          },
        );
        delay += kind === "enter" ? 40 : 18;
      }
    }
  }
}
